"use client";

import { useState, useEffect } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/pagination";
import { motion, AnimatePresence } from "framer-motion";
import {
  fadeUp,
  fade,
  stagger,
  staggerParent,
  cardItem,
  imageAnim,
  slideLeft,
  slideRight,
} from "./components/motionVariants";
import { colorPalette } from "@/utils/theme";
import { ACCOUNT_DASHBOARD_URL } from "@/utils/constants";
import useSession from "@/hooks/useSession";
import { useRouter } from "next/navigation";
import {
  QrCode,
  MapPin,
  Link2,
  Shield,
  BarChart2,
  Users,
  Megaphone,
  ClipboardList,
  CreditCard,
  FileText,
  TrendingUp,
  Package,
  Factory,
  CheckCircle,
  Plus,
  ArrowRight,
  ArrowUpRight,
  UploadCloud,
  FileCheck,
  Search,
  Zap,
  RefreshCw,
  ShoppingBag,
  DollarSign,
  AlertCircle,
  Receipt,
  Filter,
  Clock,
  BookOpen,
  BarChart3,
} from "lucide-react";

// ─── Data ────────────────────────────────────────────────────────────────────

const whyAbssFeatures = [
  {
    iconType: "gst",
    title: "GST Built-In, Not Bolted On",
    desc: "Enter a GSTIN and ABSS fills in the legal name and address. E-Invoice (IRN) and E-Way Bill are generated inside ABSS. No third-party compliance plugin, no manual re-entry into the GST portal.",
    iconBg: "bg-orange-50/70 border-orange-100/80 text-[#FB923C]",
  },
  {
    iconType: "cycle",
    title: "Lead to Ledger",
    desc: "A CRM quote becomes a sales order in ABSS Books - same customer, same items, no re-entry. Payments reflect across your CRM dashboard and your Balance Sheet.",
    iconBg: "bg-emerald-50/70 border-emerald-100/80 text-[#4ADE80]",
  },
  {
    iconType: "qr",
    title: "QR Code Lead Capture",
    desc: "Print a QR code for your stall, showroom, or event. A prospect scans it, fills a short form - ABSS creates the lead automatically. No app or login needed for the visitor, no manual entry.",
    iconBg: "bg-purple-50/70 border-purple-100/80 text-purple-600",
  },
  {
    iconType: "geo",
    title: "Geographic Sales Intelligence",
    desc: "See revenue and pipeline by state, with region-wise performance across expos and campaigns - so you know which geographies drive growth and which are going cold.",
    iconBg: "bg-blue-50/70 border-blue-100/80 text-blue-600",
  },
];

const benefitsCards = [
  {
    icon: "/images/automation-icon.png",
    title: "Recurring Billing, Set Once",
    text: "Set up a recurring invoice once and ABSS creates it on schedule, with the same items, terms and due date every cycle. No missed billing cycles, no copy-paste.",
  },
  {
    icon: "/images/ai-icon.png",
    title: "Inventory Always in Sync",
    text: "Every delivery challan or invoice deducts stock, and every production order consumes raw materials. Your stock count is always live, with no end-of-day reconciliation.",
  },
  {
    icon: "/images/collaboration-icon.png",
    title: "Role-Based Access for Your Team",
    text: "Your sales rep sees CRM and orders. Your accountant sees ledgers and bills. Your warehouse manager sees inventory. Granular roles - no data leaks between departments.",
  },
  {
    icon: "/images/scalability-icon.png",
    title: "Real-Time Financial Reports",
    text: "P&L, Balance Sheet, General Ledger, and debtors aging update live as you invoice and collect - no waiting for month-end to see where you stand.",
  },
  {
    icon: "/images/automation-icon.png",
    title: "Multi-Warehouse & Batch Tracking",
    text: "Track stock across multiple warehouses with batch numbers and expiry dates, and move goods between locations with inventory transfers, all in real time.",
  },
  {
    icon: "/images/ai-icon.png",
    title: "Customer Self-Order Portal (Add-on)",
    text: "Let customers place orders directly through their own portal. Orders land in ABSS Books instantly: no calls, no WhatsApp, no re-entry by your team.",
  },
];

const testimonials = [
  {
    name: "Shubham Patel",
    role: "Director, Om Sara Pvt. Ltd. · Ahmedabad",
    quote:
      "PCB manufacturing means tracking every component that goes into a board. With ABSS, our production orders consume raw materials automatically, stock stays accurate, and finished boards are ready to invoice with E-Invoice in the same system. Sales enquiries come in through CRM and flow straight into Books.",
    metric: "Production to E-Invoice,",
    metricLabel: "one system",
  },
  {
    name: "Nikunj Patel",
    role: "Founder, Om Power Electronics Pvt. Ltd. · Ahmedabad",
    quote:
      "Making stabilizers means buying a lot of components from many vendors and storing them across different warehouses. ABSS Books gives us the full purchase cycle, from PO to goods receipt to bill, and we can see exactly what stock is in which warehouse at any time. With CRM on the sales side, everything stays in one system.",
    metric: "Multi-warehouse stock,",
    metricLabel: "always accurate",
  },
  {
    name: "Sejal Dudhat",
    role: "Director, Victor Sales Impex Pvt. Ltd. · Ahmedabad",
    quote:
      "Our sales team makes a lot of calls every day, and before ABSS we had no clear picture of who was following up with whom. Now every call is logged against the lead in ABSS CRM, so we can see each salesperson's activity and how many of their leads turn into orders. The orders then flow straight into Books.",
    metric: "Every call tracked,",
    metricLabel: "every lead followed up",
  },
];

const indiaTrustPills = [
  "1-Click E-Invoice",
  "E-Way Bill in Seconds",
  "GSTIN → Auto-filled",
  "Lead to Ledger",
];

const sectionPadding = "px-5 sm:px-6 md:px-10 lg:px-12";

// ─── Component ────────────────────────────────────────────────────────────────

const FeatureIcon = ({ type }) => {
  switch (type) {
    case "gst":
      return (
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          <path d="m9 12 2 2 4-4" strokeWidth="2.8" />
        </svg>
      );
    case "cycle":
      return (
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M21.5 2v6h-6M21.34 15.57a10 10 0 1 1-.57-8.38l5.67-5.67" />
          <circle cx="12" cy="12" r="3" />
        </svg>
      );
    case "qr":
      return (
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <rect x="3" y="3" width="7" height="7" rx="1.5" />
          <rect x="14" y="3" width="7" height="7" rx="1.5" />
          <rect x="3" y="14" width="7" height="7" rx="1.5" />
          <path
            d="M14 14h2v2h-2zm4 4h2v2h-2zm0-4h2v2h-2zm-4 4h2v2h-2z"
            strokeWidth="1.8"
          />
        </svg>
      );
    case "geo":
      return (
        <svg
          className="w-6 h-6"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <path d="M12 2a14.5 14.5 0 0 0 0 20 14.5 14.5 0 0 0 0-20" />
          <path d="M2 12h20" />
        </svg>
      );
    default:
      return null;
  }
};

const BenefitIcon = ({ title }) => {
  switch (title) {
    case "Automated Recurring Billing":
      return <RefreshCw className="w-5 h-5 text-[#FB923C]" />;
    case "Inventory Always in Sync":
      return <Package className="w-5 h-5 text-[#4ADE80]" />;
    case "Role-Based Access for Your Team":
      return <Users className="w-5 h-5 text-[#C084FC]" />;
    case "Real-Time Financial Reports":
      return <FileCheck className="w-5 h-5 text-[#1B999A]" />;
    case "Multi-Warehouse & Batch Tracking":
      return <Package className="w-5 h-5 text-[#FB923C]" />;
    case "Customer Self-Order Portal":
      return <QrCode className="w-5 h-5 text-[#C084FC]" />;
    default:
      return <FileText className="w-5 h-5 text-[#171717]" />;
  }
};

const getIconBg = (title) => {
  switch (title) {
    case "Automated Recurring Billing":
      return "bg-orange-50/70 border-orange-100/80 text-[#FB923C]";
    case "Inventory Always in Sync":
      return "bg-emerald-50/70 border-emerald-100/80 text-[#4ADE80]";
    case "Role-Based Access for Your Team":
      return "bg-purple-50/70 border-purple-100/80 text-[#C084FC]";
    case "Real-Time Financial Reports":
      return "bg-blue-50/70 border-blue-100/80 text-[#1B999A]";
    case "Multi-Warehouse & Batch Tracking":
      return "bg-amber-50/70 border-amber-100/80 text-[#FB923C]";
    case "Customer Self-Order Portal":
      return "bg-indigo-50/70 border-indigo-100/80 text-[#C084FC]";
    default:
      return "bg-neutral-50/70 border-neutral-100/80 text-[#171717]";
  }
};

const CrmInteractiveMock = () => {
  return (
    <img
      src="/images/crm.png"
      alt="ABSS CRM Dashboard"
      className="w-full h-auto block rounded-lg sm:rounded-xl"
    />
  );
};

const BooksInteractiveMock = () => {
  return (
    <img
      src="/images/books.png"
      alt="ABSS Books Dashboard"
      className="w-full h-auto block rounded-lg sm:rounded-xl"
    />
  );
};



const RoadmapTimeline = () => {
  const steps = [
    {
      phase: "Phase 1",
      title: "ABSS Books",
      desc: "Accounting, GST, sales, purchases, inventory, banking and reports. Everything you need to run your business from day one.",
      status: "Available Now",
      align: "center",
      phaseAccent: true,
      badgeType: "available",
    },
    {
      phase: "Phase 2",
      title: "ABSS CRM",
      desc: "Manage leads, quotations, follow-ups, visits and campaigns, all connected directly to your accounting data.",
      status: "Available Now",
      align: "center",
      phaseAccent: false,
      badgeType: "available",
    },
    {
      phase: "Phase 3",
      title: "ABSS Teams",
      desc: "Collaborate across departments with tasks, approvals, internal chat and shared workflows, all built on the same business data.",
      status: "Coming Soon",
      align: "center",
      phaseAccent: false,
      badgeType: "comingsoon",
    },
    {
      phase: "Phase 4",
      title: "ABSS Business Mall",
      desc: "A B2B marketplace to find suppliers, distributors and service providers, and buy from them without leaving ABSS.",
      status: "Planned",
      align: "center",
      phaseAccent: false,
      badgeType: "planned",
    },
    {
      phase: "Phase 5",
      title: "ABSS AI",
      desc: "Your business assistant. Ask questions in plain English, get reports instantly, automate repetitive work and spot trends early.",
      status: "Vision",
      align: "center",
      phaseAccent: false,
      badgeType: "vision",
    },
  ];

  const StatusBadge = ({ type, label, align }) => {
    const alignClass =
      align === "left"
        ? "self-start"
        : align === "right"
          ? "self-end"
          : "self-center";

    const variants = {
      available: { wrap: "bg-[#ECFDF3] text-[#067647]", dot: "bg-[#12B76A]", dotFirst: true },
      comingsoon: { wrap: "bg-[#F3F4F6] text-[#171717]", dot: "bg-[#171717]", dotFirst: true },
      planned: { wrap: "bg-[#F3E8FF] text-[#7E22CE]", dot: "bg-[#7E22CE]", dotFirst: false },
      vision: { wrap: "bg-[#EFF6FF] text-[#1D4ED8]", dot: "bg-[#1D4ED8]", dotFirst: false },
    };
    const v = variants[type] || variants.comingsoon;

    return (
      <span
        className={`inline-flex items-center gap-1.5 ${v.wrap} rounded-full px-2.5 py-1 text-[9px] geist-bold uppercase tracking-wide ${alignClass}`}
      >
        {v.dotFirst && <span className={`w-1.5 h-1.5 rounded-full ${v.dot} shrink-0`} />}
        {label}
        {!v.dotFirst && <span className={`w-1.5 h-1.5 rounded-full ${v.dot} shrink-0`} />}
      </span>
    );
  };

  const textAlign = (align) =>
    align === "left"
      ? "text-left"
      : align === "right"
        ? "text-right"
        : "text-center";

  const itemsAlign = (align) =>
    align === "left"
      ? "items-start"
      : align === "right"
        ? "items-end"
        : "items-center";

  return (
    <div className="w-full relative mt-12 sm:mt-16 py-4">
      {/* Desktop - horizontal cards with center connector line */}
      <div className="hidden lg:block relative px-2 sm:px-4">
        <div className="relative">
          {/* Connector: dark through phase 1–3, light gray to phase 4 */}
          <div
            className="absolute left-[10%] right-[10%] top-1/2 -translate-y-1/2 h-[2px] flex z-0 pointer-events-none"
            aria-hidden="true"
          >
            <div className="w-1/4 bg-[#171717]" />
            <div className="flex-1 bg-[#E5E5E5]" />
          </div>

          <div className="grid grid-cols-5 gap-3 sm:gap-5 relative z-10">
            {steps.map((step) => (
              <div
                key={step.phase}
                className={`flex flex-col min-h-[168px] bg-white border border-[#E5E5E5] rounded-2xl px-4 py-4 shadow-sm ${textAlign(step.align)} ${itemsAlign(step.align)}`}
              >
                <span
                  className={`text-[10px] uppercase geist-bold tracking-widest mb-1.5 ${step.phaseAccent
                    ? "text-[#2563eb] bg-[#EFF6FF] px-1.5 py-0.5 rounded inline-block"
                    : "text-[#737373]"
                    }`}
                >
                  {step.phase}
                </span>
                <h4 className="text-[15px] geist-bold text-[#171717] leading-snug mb-1.5">
                  {step.title}
                </h4>
                <p className="text-[12px] text-[#525252] geist-medium leading-relaxed mb-4 flex-1">
                  {step.desc}
                </p>
                <StatusBadge
                  type={step.badgeType}
                  label={step.status}
                  align={step.align}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Mobile / tablet - vertical stack */}
      <div className="lg:hidden space-y-4 sm:space-y-5">
        {steps.map((step, idx) => (
          <div key={step.phase} className="w-full max-w-[420px] mx-auto">
            <div className="bg-white border border-[#E5E5E5] rounded-xl sm:rounded-2xl px-4 py-4 shadow-sm flex flex-col text-center items-center">
              <span
                className={`text-[10px] uppercase geist-bold tracking-widest mb-1.5 ${step.phaseAccent
                  ? "text-[#2563eb] bg-[#EFF6FF] px-1.5 py-0.5 rounded inline-block"
                  : "text-[#737373]"
                  }`}
              >
                {step.phase}
              </span>
              <h4 className="text-[14px] sm:text-[15px] geist-bold text-[#171717] leading-snug mb-1.5">
                {step.title}
              </h4>
              <p className="text-[12px] sm:text-[13px] text-[#525252] geist-medium leading-relaxed mb-3">
                {step.desc}
              </p>
              <StatusBadge
                type={step.badgeType}
                label={step.status}
                align="center"
              />
            </div>
            {idx < steps.length - 1 && (
              <div className="flex justify-center my-3 sm:my-4" aria-hidden="true">
                <div
                  className={`w-0.5 h-5 sm:h-6 rounded-full ${idx < 2 ? "bg-[#171717]" : "bg-[#E5E5E5]"}`}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default function Home() {
  const [activeTab, setActiveTab] = useState("books");
  const router = useRouter();
  const { status, user } = useSession();
  const isAuthenticated = status === "authenticated" && user;

  return (
    <div className="overflow-x-hidden w-full">
      {/* ── Hero ── */}
      <section className="relative w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        {/* Background work: Grid pattern & pulsing ambient glows */}
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <div className="absolute top-[-25%] left-[-15%] w-[60%] h-[80%] rounded-full bg-gradient-to-br from-orange-200/40 to-amber-100/10 blur-[130px] animate-aurora-pulse" />
          <div className="absolute bottom-[-15%] right-[-10%] w-[55%] h-[75%] rounded-full bg-gradient-to-tr from-blue-200/30 to-indigo-100/10 blur-[130px] animate-aurora-pulse [animation-delay:3s]" />
          <div className="absolute inset-0 bg-grid-mesh opacity-80" />
        </div>

        <div className="relative z-10 max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12 lg:py-16 sm:py-24 py-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
            className="max-w-4xl mx-auto w-full"
          >
            {/* Badge */}
            <motion.div
              variants={fadeUp}
              className="flex justify-center mb-5 sm:mb-6"
            >
              <span className="inline-flex items-center gap-1.5 border border-[#e5e5e5] rounded-full px-4 py-1.5 text-[13px] text-[#262626] geist-semibold bg-white/80 backdrop-blur-sm shadow-sm">
                <span className="w-2 h-2 rounded-full bg-[#DF5D01] inline-block animate-pulse"></span>
                Built for Indian SMEs
              </span>
            </motion.div>

            {/* Heading */}
            <motion.h1
              variants={fadeUp}
              className="text-[26px] sm:text-[48px] md:text-[60px] lg:text-[66px] leading-[1.15] sm:leading-[1.12] text-[#171717] mb-5 sm:mb-6 sentient-bold tracking-tight px-1"
            >
              One Platform for Sales,
              <br className="hidden sm:block" />
              Accounting &amp; GST Compliance
            </motion.h1>

            {/* Paragraph */}
            <motion.p
              variants={fadeUp}
              className="text-[15px] sm:text-lg md:text-[21px] font-medium leading-relaxed text-[#262626] max-w-[640px] mx-auto mb-8 sm:mb-10 geist-medium px-1"
            >
              Track leads, send quotes, raise GST invoices and collect payments
              in one connected system. ABSS CRM and ABSS Books share the same
              data, so nothing gets entered twice.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div
              variants={fadeUp}
              className="flex flex-col sm:flex-row justify-center items-stretch sm:items-center gap-3 sm:gap-4 mb-10 w-full max-w-[320px] sm:max-w-none mx-auto"
            >
              {isAuthenticated ? (
                <a
                  href={ACCOUNT_DASHBOARD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 text-white font-semibold h-[46px] sm:h-[48px] px-8 text-sm sm:text-base rounded-lg bg-black shadow-md hover:shadow-lg w-full sm:w-auto geist-semibold"
                >
                  Access Your Apps
                  <ArrowUpRight className="w-4 h-4" />
                </a>
              ) : (
                <>
                  <button
                    className="cursor-pointer text-white font-semibold h-[46px] sm:h-[48px] px-8 text-sm sm:text-base rounded-lg bg-black shadow-md hover:shadow-lg w-full sm:w-auto geist-semibold"
                    onClick={() => router.push("/contact")}
                  >
                    Start Free Trial
                  </button>

                  <button
                    className="border cursor-pointer border-[#E5E5E5] bg-white/80 backdrop-blur-sm h-[46px] sm:h-[48px] text-[#262626] font-semibold px-8 text-sm sm:text-base rounded-lg hover:bg-neutral-50 hover:text-black transition-all duration-300 w-full sm:w-auto geist-semibold"
                    onClick={() => router.push("/contact")}
                  >
                    Book a Demo →
                  </button>
                </>
              )}
            </motion.div>

            {/* India Trust Pills */}
            <motion.div
              variants={stagger}
              className="flex flex-wrap justify-center gap-2 sm:gap-2.5 px-1"
            >
              {indiaTrustPills.map((pill) => (
                <motion.span
                  key={pill}
                  variants={fadeUp}
                  className="border border-[#E5E5E5] rounded-full px-3 sm:px-3.5 py-1.5 text-xs sm:text-[13px] text-[#262626] geist-medium bg-white/70 backdrop-blur-sm shadow-sm hover:border-[#df5d01]/30 transition-all"
                >
                  {pill}
                </motion.span>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ── Module Switcher ── */}
      <section className="w-full bg-[#f5f5f5] pb-10 sm:pb-24 overflow-hidden relative border-b border-[#e5e5e5]">
        {/* Curved Background */}
        <motion.div
          initial="hidden"
          whileInView="show"
          variants={fade}
          viewport={{ once: true }}
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[min(100%,800px)] h-[74px] sm:h-[95px] pointer-events-none z-0"
        >
          <img
            src="/images/curve-left.png"
            alt=""
            className="absolute left-0 top-0 w-[48px] h-[48px] sm:w-[85px] sm:h-[74px]"
          />
          <div className="absolute left-[44px] right-[44px] sm:left-[80px] sm:right-[80px] top-0 h-[81%] lg:h-[74px] bg-white"></div>
          <img
            src="/images/curve-right.png"
            alt=""
            className="absolute right-0 top-0 w-[48px] h-[48px] sm:w-[85px] sm:h-[74px]"
          />
        </motion.div>

        <div className="relative z-10 mx-auto max-w-[1400px] px-5 sm:px-6 md:px-10 lg:px-12">
          {/* Tab Buttons */}
          <motion.div
            initial="hidden"
            whileInView="show"
            variants={fadeUp}
            viewport={{ once: true, amount: 0.2 }}
            className="flex flex-nowrap items-center justify-center gap-1.5 sm:gap-3 mb-3 sm:mb-6 mt-3 sm:mt-5 w-full"
          >
            {["books", "crm", "teams"].map((tab) => {
              const isActive = activeTab === tab;
              const icons = {
                books: BookOpen,
                crm: BarChart3,
                teams: Users,
              };
              const iconColor = {
                books: "text-[#1785B6]",
                crm: "text-[#1B999A]",
                teams: "text-[#C084FC]",
              };
              const labels = {
                books: "ABSS Books",
                crm: "ABSS CRM",
                teams: "ABSS Teams",
              };
              const Icon = icons[tab];
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`
                    flex items-center justify-center gap-1 sm:gap-2 px-2.5 sm:px-5
                    h-8 sm:h-[34px] flex-1 sm:flex-none min-w-0 sm:min-w-max max-w-[140px] sm:max-w-none
                    rounded-lg border border-[#e5e5e5] font-medium shadow-sm transition-all duration-300
                    text-[11px] sm:text-base whitespace-nowrap cursor-pointer
                    ${isActive ? "bg-white text-[#262626] shadow-md" : "bg-[#f5f5f5] text-[#525252]"}
                  `}
                >
                  <Icon
                    className={`w-3.5 h-3.5 sm:w-[18px] sm:h-[18px] flex-shrink-0 ${iconColor[tab]}`}
                    strokeWidth={2}
                  />
                  <span className="truncate">{labels[tab]}</span>
                </button>
              );
            })}
          </motion.div>

          {/* Tab description line - fixed height to prevent layout shift on tab change */}
          <motion.div
            initial="hidden"
            whileInView="show"
            variants={fadeUp}
            viewport={{ once: true }}
            className="relative text-center mb-6 sm:mb-4 min-h-[2.75rem] sm:min-h-[2.75rem] px-1"
          >
            <AnimatePresence mode="wait">
              {activeTab === "books" && (
                <motion.p
                  key="books-desc"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-x-0 top-0 text-[12px] sm:text-[14px] leading-snug text-[#525252] geist-regular px-1 sm:px-2"
                >
                  Complete accounting - sales, purchase, inventory, production,
                  GST &amp; reports
                </motion.p>
              )}
              {activeTab === "crm" && (
                <motion.p
                  key="crm-desc"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-x-0 top-0 text-[12px] sm:text-[14px] leading-snug text-[#525252] geist-regular px-1 sm:px-2"
                >
                  Lead capture, pipeline management, campaigns, QR codes &amp;
                  geographic insights
                </motion.p>
              )}
              {activeTab === "teams" && (
                <motion.p
                  key="teams-desc"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                  className="absolute inset-x-0 top-0 text-[12px] sm:text-[14px] leading-snug text-[#525252] geist-regular px-1 sm:px-2"
                >
                  Team collaboration &amp; task management - coming soon
                </motion.p>
              )}
            </AnimatePresence>
          </motion.div>

          {/* Image/Mock Card Container */}
          <motion.div
            initial="hidden"
            whileInView="show"
            variants={fadeUp}
            viewport={{ once: true, amount: 0.3 }}
            className="w-full max-w-[908px] mx-auto overflow-hidden border p-1.5 sm:p-3 rounded-xl sm:rounded-2xl border-[#e5e5e5] bg-white shadow-lg sm:shadow-xl"
          >
            <div className="relative w-full rounded-lg sm:rounded-xl overflow-hidden border border-[#e5e5e5] bg-[#f8fafc]">
              <div
                style={{ display: activeTab === "books" ? "block" : "none" }}
                className="w-full h-auto"
              >
                <BooksInteractiveMock />
              </div>

              <div
                style={{ display: activeTab === "crm" ? "block" : "none" }}
                className="w-full h-auto"
              >
                <CrmInteractiveMock />
              </div>

              <div
                style={{ display: activeTab === "teams" ? "flex" : "none" }}
                className="w-full aspect-[2256/1302] min-h-[260px] flex-col justify-center items-center bg-white gap-2.5 sm:gap-3 overflow-hidden px-4"
              >
                <span className="border border-[#E5E5E5] rounded-full px-3 py-1 text-[11px] text-[#525252] geist-regular bg-[#FAFAFA]">
                  Coming Soon
                </span>
                <h2 className="text-sm sm:text-lg sentient-regular text-[#171717]">
                  ABSS Teams
                </h2>
                <p className="text-[12px] sm:text-[13px] text-[#525252] geist-regular max-w-[260px] text-center leading-snug">
                  Team collaboration and task management - launching Q2 2026
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── Why ABSS? ── */}
      <section className="w-full bg-white border-b border-[#E5E5E5]">
        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12 py-12 sm:py-16 md:py-20"
        >
          {/* Eyebrow */}
          <motion.p
            variants={fadeUp}
            className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3 text-center md:text-left"
          >
            Why ABSS?
          </motion.p>

          {/* Heading */}
          <motion.h2
            variants={fadeUp}
            className="text-[22px] sm:text-[38px] md:text-[44px] leading-[1.25] sm:leading-[1.2] text-[#171717] mb-3 sm:mb-4 sentient-medium max-w-[920px] mx-auto md:mx-0 text-center md:text-left"
          >
            Built for India from day one,
            <br className="hidden md:block" /> not adapted for it later.
          </motion.h2>

          {/* Subheading */}
          <motion.p
            variants={fadeUp}
            className="max-w-[680px] mx-auto md:mx-0 text-[14px] sm:text-[17px] md:text-[18px] leading-[1.65] sm:leading-[1.7] text-[#525252] mb-8 sm:mb-12 geist-regular text-center md:text-left"
          >
            Most business software is designed for the West and patched for
            GST afterwards. ABSS started with GST and the way Indian SMEs
            actually work: challans, godowns, credit terms, and follow-ups on
            WhatsApp. Every feature solves a problem a real Indian business
            had.
          </motion.p>

          {/* Feature Grid */}
          <motion.div
            variants={staggerParent}
            className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-10 md:gap-14"
          >
            {whyAbssFeatures.map((item, i) => (
              <motion.div
                key={i}
                variants={cardItem}
                className="group flex flex-col items-center md:items-start text-center md:text-left gap-3 sm:gap-5 border border-neutral-100/80 sm:border-transparent hover:border-neutral-200/50 hover:bg-neutral-50/40 p-4 sm:p-6 rounded-xl sm:rounded-2xl transition-all duration-300 sm:-m-6"
              >
                <div
                  className={`flex items-center justify-center w-11 h-11 sm:w-12 sm:h-12 rounded-xl border ${item.iconBg} shadow-sm transition-transform duration-300 group-hover:scale-105 shrink-0`}
                >
                  <FeatureIcon type={item.iconType} />
                </div>
                <div className="min-w-0 w-full">
                  <h3 className="text-[18px] sm:text-[22px] md:text-[24px] text-[#171717] mb-1.5 sm:mb-2 sentient-medium leading-snug">
                    {item.title}
                  </h3>
                  <p className="text-[#333333] text-[13px] sm:text-[15px] md:text-[16px] leading-relaxed geist-medium max-w-[480px]">
                    {item.desc}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      {/* ── ABSS Suite Modules ── */}
      <section className="w-full text-center pt-12 sm:pt-16 md:pt-20 pb-8 sm:pb-10 bg-white border-[#E5E5E5] max-w-[1160px] mx-auto border-b px-5 sm:px-6 md:px-10 lg:px-12">
        <div className="w-full max-w-[1080px] mx-auto min-w-0">
          {/* Headings */}
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
            className="mb-8 sm:mb-14 max-w-[560px] mx-auto px-1"
          >
            <motion.p
              variants={fadeUp}
              className="text-[#DF5D01] text-[13px] sm:text-[14px] geist-medium mb-2.5 sm:mb-3"
            >
              The ABSS Suite
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="text-[24px] sm:text-[38px] md:text-[44px] leading-[1.25] sm:leading-[1.2] text-[#171717] mb-4 sm:mb-5 sentient-medium"
            >
              One Suite. Every Workflow Connected.
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="text-[14px] sm:text-[17px] md:text-[18px] font-normal leading-[1.65] sm:leading-[1.7] text-[#525252] mx-auto geist-regular"
            >
              Every ABSS product runs on the same data, so a CRM quote becomes
              a sales order in Books, and each new product plugs into what
              you&apos;ve already built. No exports. No duplicate entry.
            </motion.p>
          </motion.div>

          {/* Block 1 - Books */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-8 lg:gap-12 items-center bg-gradient-to-br from-white to-[#FAF9F8] border border-[#e5e5e5] rounded-xl sm:rounded-3xl p-3.5 sm:p-8 lg:p-12 shadow-md sm:shadow-lg hover:shadow-xl transition-all duration-500 mb-6 sm:mb-10 hover:border-neutral-300/80 overflow-hidden min-w-0">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={slideRight}
              className="order-2 lg:order-1 flex justify-center items-center w-full min-w-0 overflow-hidden"
            >
              <img
                src="/images/home-books-feature.jpg"
                alt="ABSS Books Accounting and AI OCR"
                className="w-full aspect-[4/3] object-cover rounded-xl sm:rounded-2xl"
              />
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={slideLeft}
              className="order-1 lg:order-2 text-center lg:text-left flex flex-col items-center lg:items-start space-y-4 sm:space-y-5 lg:pl-6 min-w-0"
            >
              <span className="inline-flex items-center gap-1.5 border border-orange-100 rounded-full px-3 sm:px-3.5 py-1.5 text-[11px] geist-semibold bg-orange-50/40 text-orange-700 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-500 inline-block animate-pulse"></span>
                ABSS Books
              </span>

              <h2 className="text-[22px] sm:text-3xl md:text-[34px] leading-[1.25] text-[#171717] sentient-medium tracking-tight max-w-[480px] lg:max-w-none">
                End-to-end accounting built for Indian SMEs.
              </h2>

              <div className="text-[#333333] text-[13px] sm:text-[15px] leading-relaxed geist-medium space-y-3 sm:space-y-4 max-w-full lg:max-w-[460px] mx-auto lg:mx-0">
                <p>
                  Quotes to payments, purchase to stock, production to P&amp;L.
                  Every transaction flows into your books automatically, with a
                  dedicated accountant view, chart of accounts and manual
                  journals for your CA.
                </p>
              </div>

              {/* Feature bullets */}
              <ul className="space-y-2 sm:space-y-2.5 w-full max-w-[300px] lg:max-w-none mx-auto lg:mx-0 text-left">
                {[
                  "Sales, purchase, inventory & banking",
                  "E-Invoice & E-Way Bill",
                  "Production orders & BOM tracking",
                  "P&L, Balance Sheet & 12+ reports",
                ].map((f) => (
                  <li
                    key={f}
                    className="flex items-start sm:items-center gap-2.5 text-[12px] sm:text-[14px] text-[#262626] geist-semibold"
                  >
                    <span className="w-4 h-4 sm:w-[18px] sm:h-[18px] mt-0.5 sm:mt-0 rounded-full bg-orange-50 border border-orange-100/80 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                        <path
                          d="M1 3L3 5L7 1"
                          stroke="#ea580c"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="min-w-0">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="pt-1 sm:pt-2">
                <a
                  href="/features/books"
                  className="inline-flex items-center gap-1.5 text-sm sm:text-base geist-semibold text-[#DF5D01] hover:text-[#c45200] transition-colors group"
                >
                  Explore ABSS Books
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </motion.div>
          </div>

          {/* Block 2 - CRM */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 sm:gap-8 lg:gap-12 items-center bg-gradient-to-br from-white to-[#FAF9F8] border border-[#e5e5e5] rounded-xl sm:rounded-3xl p-3.5 sm:p-8 lg:p-12 shadow-md sm:shadow-lg hover:shadow-xl transition-all duration-500 hover:border-neutral-300/80 overflow-hidden min-w-0">
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={slideRight}
              className="order-1 lg:order-1 text-center lg:text-left flex flex-col items-center lg:items-start space-y-4 sm:space-y-5 lg:pr-6 min-w-0"
            >
              <span className="inline-flex items-center gap-1.5 border border-emerald-100 rounded-full px-3 sm:px-3.5 py-1.5 text-[11px] geist-semibold bg-emerald-50/40 text-emerald-700 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 inline-block animate-pulse"></span>
                ABSS CRM
              </span>

              <h2 className="text-[22px] sm:text-3xl md:text-[34px] leading-[1.25] text-[#171717] sentient-medium tracking-tight max-w-[480px] lg:max-w-none">
                Convert more leads. Close deals faster.
              </h2>

              <div className="text-[#333333] text-[13px] sm:text-[15px] leading-relaxed geist-medium space-y-3 sm:space-y-4 max-w-full lg:max-w-[460px] mx-auto lg:mx-0">
                <p>
                  Track every lead from first enquiry to confirmed order. Assign
                  leads to your team, log visits and follow-ups, and see which
                  campaigns bring in business. When a deal is won, the order
                  flows into ABSS Books, ready to deliver and invoice.
                </p>
              </div>

              {/* Feature bullets */}
              <ul className="space-y-2 sm:space-y-2.5 w-full max-w-[300px] lg:max-w-none mx-auto lg:mx-0 text-left">
                {[
                  "Lead management & bulk import",
                  "Quotations, tasks & field visits",
                  "QR code lead capture",
                  "Campaign tracking & team performance",
                ].map((f) => (
                  <li
                    key={f}
                    className="flex items-start sm:items-center gap-2.5 text-[12px] sm:text-[14px] text-[#262626] geist-semibold"
                  >
                    <span className="w-4 h-4 sm:w-[18px] sm:h-[18px] mt-0.5 sm:mt-0 rounded-full bg-emerald-50 border border-emerald-100/80 flex items-center justify-center flex-shrink-0 shadow-sm">
                      <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                        <path
                          d="M1 3L3 5L7 1"
                          stroke="#10b981"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                    <span className="min-w-0">{f}</span>
                  </li>
                ))}
              </ul>

              <div className="pt-1 sm:pt-2">
                <a
                  href="/features/crm"
                  className="inline-flex items-center gap-1.5 text-sm sm:text-base geist-semibold text-[#DF5D01] hover:text-[#c45200] transition-colors group"
                >
                  Explore ABSS CRM
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </a>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.3 }}
              variants={slideLeft}
              className="order-2 lg:order-2 flex justify-center items-center w-full min-w-0 overflow-hidden"
            >
              <img
                src="/images/home-crm-feature.jpg"
                alt="ABSS CRM Sales Pipeline"
                className="w-full aspect-[4/3] object-cover rounded-xl sm:rounded-2xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* ── Core Benefits ── */}
      <section className="relative w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        {/* Ambient background glows */}
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <div className="absolute top-[20%] right-[-10%] w-[45%] h-[60%] rounded-full bg-gradient-to-br from-orange-100/20 to-transparent blur-[120px] animate-aurora-pulse" />
          <div className="absolute bottom-[10%] left-[-10%] w-[45%] h-[60%] rounded-full bg-gradient-to-tr from-indigo-50/20 to-transparent blur-[120px] animate-aurora-pulse [animation-delay:4s]" />
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          variants={stagger}
          className="relative z-10 max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12 py-12 sm:py-16 md:py-24"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 lg:gap-20 items-center justify-items-center min-w-0">
            {/* Left Text */}
            <motion.div
              variants={slideLeft}
              className="w-full max-w-[520px] md:max-w-none text-center md:text-left space-y-5 sm:space-y-7 min-w-0 flex flex-col items-center md:items-start"
            >
              <span className="inline-flex items-center gap-1.5 border border-orange-100 rounded-full px-3 sm:px-3.5 py-1.5 text-[11px] geist-semibold bg-orange-50/40 text-orange-700 shadow-sm">
                <span className="w-1.5 h-1.5 rounded-full bg-[#DF5D01] inline-block animate-pulse"></span>
                Core Benefits
              </span>
              <h2 className="w-full max-w-[480px] mx-auto md:mx-0 text-[22px] sm:text-3xl md:text-[44px] leading-[1.25] sm:leading-[1.2] text-[#171717] sentient-medium tracking-tight">
                Save time on compliance. Reduce billing errors. Decide faster.
              </h2>
              <p className="text-[#333333] text-[13px] sm:text-[15px] leading-relaxed geist-medium max-w-[420px] mx-auto md:mx-0">
                Every feature in ABSS is built around a pain Indian SMEs face
                every day, from GST invoicing to inventory tracking to lead
                follow-up.
              </p>
              <div className="pt-1 sm:pt-2 w-full max-w-[320px] sm:max-w-none flex justify-center md:justify-start">
                <button
                  className="cursor-pointer text-white font-semibold h-[46px] px-8 text-sm rounded-lg bg-gradient-to-r from-[#DF5D01] to-[#e46a13] hover:from-[#c45200] hover:to-[#df5d01] transition-all duration-300 shadow-md hover:shadow-lg w-full sm:w-auto geist-semibold flex items-center justify-center gap-1.5"
                  onClick={() => router.push("/features/books")}
                >
                  Explore ABSS Books
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </motion.div>

            {/* Right Auto-Scroll Cards */}
            <motion.div
              variants={slideRight}
              className="relative w-full max-w-[510px] h-[280px] sm:h-[380px] md:h-[440px] mx-auto overflow-hidden rounded-xl sm:rounded-2xl scroll-fade-mask min-w-0"
            >
              <div className="animate-scroll-up flex flex-col space-y-3 sm:space-y-5">
                {[...Array(2)].map((_, groupIdx) => (
                  <div
                    key={groupIdx}
                    className="flex flex-col space-y-3 sm:space-y-5 px-0.5 sm:px-2"
                  >
                    {benefitsCards.map((item, idx) => (
                      <motion.div
                        key={idx}
                        whileHover={{
                          scale: 1.02,
                          boxShadow: "0 12px 30px rgba(0,0,0,0.06)",
                          borderColor: "#df5d0130",
                        }}
                        transition={{ duration: 0.25 }}
                        className="w-full flex items-start gap-3 sm:gap-5 px-3.5 sm:px-5 py-3.5 sm:py-[18px] border border-[#E5E5E5]/80 rounded-xl sm:rounded-2xl bg-white/95 backdrop-blur-sm shadow-sm hover:border-[#df5d01]/30 transition-all min-w-0"
                      >
                        <div
                          className={`flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl border ${getIconBg(item.title)} flex-shrink-0 shadow-sm`}
                        >
                          <BenefitIcon title={item.title} />
                        </div>
                        <div className="flex flex-col text-left flex-1 min-w-0">
                          <h3 className="text-[14px] sm:text-base font-semibold text-[#171717] geist-semibold tracking-tight mb-1">
                            {item.title}
                          </h3>
                          <p className="text-[#333333] text-[12px] sm:text-[13px] leading-relaxed geist-medium">
                            {item.text}
                          </p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ── Designed to Grow ── */}
      <section className="relative w-full bg-white border-b border-[#E5E5E5] overflow-hidden">
        {/* Ambient background glows */}
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <div className="absolute top-[30%] left-[-15%] w-[45%] h-[60%] rounded-full bg-gradient-to-br from-orange-100/10 to-transparent blur-[120px] animate-aurora-pulse" />
          <div className="absolute bottom-[20%] right-[-15%] w-[45%] h-[60%] rounded-full bg-gradient-to-tr from-blue-100/10 to-transparent blur-[120px] animate-aurora-pulse [animation-delay:2s]" />
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          variants={fadeUp}
          className="relative z-10 max-w-[1160px] mx-auto px-5 sm:px-6 md:px-10 lg:px-12 py-12 sm:py-16 md:py-24"
        >
          <div className="max-w-[1080px] mx-auto relative min-w-0">
            <motion.div
              variants={stagger}
              className="flex flex-col sm:flex-row sm:justify-between gap-5 sm:gap-6 mb-8 sm:mb-16 items-center sm:items-end"
            >
              <motion.div
                variants={fadeUp}
                className="w-full max-w-[540px] sm:max-w-none text-center sm:text-left space-y-3 flex flex-col items-center sm:items-start"
              >
                <span className="inline-flex items-center gap-1.5 border border-orange-100 rounded-full px-3 sm:px-3.5 py-1.5 text-[11px] geist-semibold bg-orange-50/40 text-orange-700 shadow-sm">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#DF5D01] inline-block animate-pulse"></span>
                  Roadmap
                </span>
                <h2 className="text-[22px] sm:text-3xl md:text-[44px] leading-[1.25] sm:leading-[1.2] text-[#171717] sentient-medium tracking-tight">
                  Start with Books.
                  <br className="hidden sm:block" /> Grow with ABSS.
                </h2>
                <p className="text-[#333333] text-[13px] sm:text-[16px] leading-relaxed geist-medium max-w-[540px] mx-auto sm:mx-0">
                  Begin with powerful accounting software today. As your business
                  grows, simply unlock new ABSS products—CRM, Teams, Business
                  Mall, AI, and more. One platform. One database. Zero migrations.
                </p>
              </motion.div>

            </motion.div>

            {/* Custom interactive CSS timeline */}
            <RoadmapTimeline />

            {/* Bottom caption */}
            <motion.div
              variants={fadeUp}
              className="mt-12 sm:mt-16 text-center max-w-[760px] mx-auto"
            >
              <h3 className="text-[18px] sm:text-[24px] text-[#171717] sentient-medium tracking-tight mb-2.5">
                Every product you add makes the rest smarter.
              </h3>
              <p className="text-[#333333] text-[13px] sm:text-[15px] leading-relaxed geist-medium">
                A new ABSS product starts with the customers, items and history
                you already have. No setup from scratch, no new system to learn.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* ── Testimonials ── */}
      <section className="relative w-full bg-gradient-to-b from-[#F6F7FA] to-white border-b border-neutral-200/50 overflow-hidden">
        {/* Pulsing ambient background glows */}
        <div className="absolute inset-0 z-0 select-none pointer-events-none">
          <div className="absolute top-[-10%] right-[-15%] w-[45%] h-[70%] rounded-full bg-gradient-to-br from-orange-100/10 to-transparent blur-[120px] animate-aurora-pulse" />
          <div className="absolute bottom-[-10%] left-[-15%] w-[45%] h-[70%] rounded-full bg-gradient-to-tr from-indigo-100/10 to-transparent blur-[120px] animate-aurora-pulse [animation-delay:3s]" />
          <div className="absolute inset-0 bg-grid-mesh opacity-[0.4]" />
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.25 }}
          variants={stagger}
          className="z-10 max-w-[1160px] mx-auto text-center px-5 sm:px-6 md:px-10 lg:px-12 py-12 sm:py-16 md:py-24 relative min-w-0"
        >
          <motion.div variants={fadeUp} className="flex justify-center mb-3 sm:mb-4">
            <span className="inline-flex items-center gap-1.5 border border-orange-100 rounded-full px-3 sm:px-3.5 py-1.5 text-[11px] geist-semibold bg-orange-50/40 text-orange-700 shadow-sm">
              <span className="w-2 h-2 rounded-full bg-[#DF5D01] inline-block animate-pulse"></span>
              Customer Stories
            </span>
          </motion.div>

          <motion.h2
            variants={fadeUp}
            className="w-full max-w-[720px] mx-auto text-[22px] sm:text-3xl md:text-[44px] leading-[1.25] sm:leading-[1.2] text-[#171717] sentient-medium tracking-tight mb-3 sm:mb-4 px-1"
          >
            Trusted by Indian SMEs Across Manufacturing, Distribution &amp;
            Trading
          </motion.h2>

          <motion.p
            variants={fadeUp}
            className="w-full max-w-[540px] text-[#333333] text-[13px] sm:text-[16px] leading-relaxed geist-medium mx-auto mb-8 sm:mb-12 px-1"
          >
            From production to purchase to sales follow-up - here&apos;s what
            ABSS customers say about running their business on one platform.
          </motion.p>

          {/* Mobile Swiper */}
          <motion.div variants={fadeUp} className="sm:hidden w-full max-w-[400px] mx-auto mt-2 overflow-hidden">
            <Swiper
              spaceBetween={16}
              slidesPerView={1}
              pagination={{ clickable: true }}
              modules={[Pagination]}
              className="pb-10 !overflow-visible"
            >
              {testimonials.map((t, i) => (
                <SwiperSlide key={i} className="!h-auto">
                  <div className="w-full h-full bg-white/95 border border-[#e5e5e5] rounded-xl text-left flex flex-col justify-between overflow-hidden shadow-sm backdrop-blur-sm min-w-0">
                    <div className="px-4 py-5">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-orange-50 to-orange-100/50 border border-orange-100/80 text-[#DF5D01] flex items-center justify-center font-bold font-sans shadow-sm flex-shrink-0 text-[14px]">
                          {t.name.charAt(0)}
                        </div>
                        <div className="min-w-0 text-left">
                          <h4 className="text-sm font-semibold text-[#171717] geist-semibold tracking-tight truncate">
                            {t.name}
                          </h4>
                          <p className="text-[11px] text-neutral-500 geist-medium truncate">
                            {t.role}
                          </p>
                        </div>
                      </div>
                      <p className="text-[#262626] text-[13px] leading-relaxed geist-medium mt-1">
                        &ldquo;
                        {t.quote}&rdquo;
                      </p>
                    </div>
                    <div className="bg-neutral-50/80 border-t border-neutral-100 px-4 py-3 flex items-center justify-center flex-wrap gap-x-1 text-xs geist-semibold">
                      <span className="text-[#DF5D01] font-bold text-sm tracking-tight">
                        {t.metric}
                      </span>
                      <span className="text-neutral-500 font-medium">
                        {t.metricLabel}
                      </span>
                    </div>
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
          </motion.div>

          {/* Desktop Auto-scroll */}
          <motion.div
            variants={fade}
            className="hidden sm:block relative w-full overflow-hidden min-w-0"
          >
            <div className="absolute top-0 left-0 h-full w-16 md:w-24 bg-gradient-to-r from-[#F6F7FA] via-[#F6F7FA]/40 to-transparent z-20 pointer-events-none"></div>
            <div className="absolute top-0 right-0 h-full w-16 md:w-24 bg-gradient-to-l from-[#F6F7FA] via-[#F6F7FA]/40 to-transparent z-20 pointer-events-none"></div>

            <div className="flex animate-scroll-left space-x-5 md:space-x-6 p-3 md:p-4">
              {[...Array(2)].map((_, group) => (
                <div key={group} className="flex space-x-5 md:space-x-6">
                  {testimonials.map((t, i) => (
                    <motion.div
                      key={i}
                      whileHover={{
                        scale: 1.02,
                        boxShadow: "0 12px 35px rgba(0,0,0,0.06)",
                        borderColor: "#df5d0130",
                      }}
                      transition={{ duration: 0.3 }}
                      className="w-[280px] md:w-[360px] shrink-0 bg-white/95 rounded-2xl border border-[#e5e5e5] overflow-hidden flex flex-col justify-between shadow-sm backdrop-blur-sm"
                    >
                      <div className="px-5 md:px-6 py-5 md:py-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-orange-50 to-orange-100/50 border border-orange-100/80 text-[#DF5D01] flex items-center justify-center font-bold font-sans shadow-sm flex-shrink-0 text-[15px]">
                            {t.name.charAt(0)}
                          </div>
                          <div className="min-w-0 text-left">
                            <h4 className="text-sm md:text-base font-semibold text-[#171717] geist-semibold tracking-tight">
                              {t.name}
                            </h4>
                            <p className="text-[11px] md:text-xs text-neutral-500 geist-medium">
                              {t.role}
                            </p>
                          </div>
                        </div>
                        <p className="text-[#262626] text-sm leading-relaxed geist-medium mt-1 text-left">
                          &ldquo;
                          {t.quote}&rdquo;
                        </p>
                      </div>
                      <div className="bg-neutral-50/80 border-t border-neutral-100 px-5 md:px-6 py-3.5 flex items-center justify-center text-xs geist-semibold">
                        <span className="text-[#DF5D01] font-bold text-sm tracking-tight mr-1">
                          {t.metric}
                        </span>
                        <span className="text-neutral-500 font-medium">
                          {t.metricLabel}
                        </span>
                      </div>
                    </motion.div>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>

        <style jsx>{`
          @keyframes scroll-left {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-50%);
            }
          }
          .animate-scroll-left {
            display: flex;
            animation: scroll-left 30s linear infinite;
            will-change: transform;
          }
        `}</style>
      </section>

      {/* ── Mobile App Section ── */}
      <section className="relative w-full bg-white py-12 sm:py-20 md:py-24 lg:py-32 overflow-hidden border-b border-[#E5E5E5]">
        <div className="absolute inset-0 z-0 bg-white" />
        <div
          className={`relative z-10 max-w-[1160px] mx-auto ${sectionPadding} min-w-0`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 sm:gap-10 lg:gap-8 items-center min-w-0">
            {/* Text Content */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
              variants={stagger}
              className="text-center lg:text-left flex flex-col items-center lg:items-start min-w-0"
            >
              <motion.div variants={fadeUp} className="mb-5 sm:mb-6">
                <span className="inline-flex items-center gap-2 border border-black/10 rounded-full px-3.5 sm:px-4 py-1.5 text-[11px] sm:text-[12px] geist-semibold bg-black/5 text-black shadow-sm">
                  <span className="w-2 h-2 rounded-full bg-black inline-block animate-pulse"></span>
                  Available Now
                </span>
              </motion.div>

              <motion.h2
                variants={fadeUp}
                className="text-[22px] sm:text-4xl md:text-5xl lg:text-[44px] xl:text-5xl leading-[1.2] sm:leading-[1.15] text-black sentient-medium tracking-tight mb-4 sm:mb-6 max-w-xl mx-auto lg:mx-0 px-1"
              >
                Run your business from your pocket.
              </motion.h2>

              <motion.p
                variants={fadeUp}
                className="text-neutral-600 text-[14px] sm:text-[17px] md:text-[18px] leading-relaxed geist-regular mb-7 sm:mb-10 max-w-xl mx-auto lg:mx-0 px-1"
              >
                Create invoices, track inventory, and manage your CRM directly
                from your smartphone. The ABSS mobile app brings the full power
                of our suite to iOS and Android.
              </motion.p>

              <motion.div
                variants={fadeUp}
                className="flex flex-wrap items-center justify-center lg:justify-start gap-3 sm:gap-5 w-full max-w-[320px] sm:max-w-none mx-auto lg:mx-0"
              >
                <a
                  href="https://play.google.com/store/apps/details?id=com.abss&hl=en_IN"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl rounded-xl inline-block"
                >
                  <img
                    src="/images/play-store-badge.png"
                    alt="GET IT ON Google Play"
                    className="h-[52px] sm:h-[72px] w-auto"
                  />
                </a>
                <a
                  href="https://apps.apple.com/in/app/abss/id6760467260"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="transition-transform duration-300 hover:-translate-y-1 hover:shadow-xl rounded-xl inline-block"
                >
                  <img
                    src="/images/app-store-badge.svg"
                    alt="Download on the App Store"
                    className="h-[38px] sm:h-[50px] w-auto"
                  />
                </a>
              </motion.div>
            </motion.div>

            {/* Images Content */}
            <motion.div
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, amount: 0.25 }}
              variants={fadeUp}
              className="relative flex justify-center lg:justify-end items-center w-full mt-8 sm:mt-12 lg:mt-0 min-w-0"
            >
              {/* Background Ambient Glow */}
              <div className="absolute inset-0 bg-gradient-to-tr from-[#DF5D01]/15 via-indigo-500/15 to-purple-500/15 blur-[80px] rounded-full pointer-events-none" />

              <div className="relative flex flex-row items-start justify-center gap-4 sm:gap-7 w-full max-w-[500px] sm:max-w-[580px] min-w-0 px-4 py-6">
                {/* Floating Badge 1 - Top Left */}
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{ repeat: Infinity, duration: 4.5, ease: "easeInOut" }}
                  className="absolute -top-3 left-0 sm:-top-5 sm:left-2 z-30 bg-white/95 backdrop-blur-md px-3 sm:px-4 py-1.5 rounded-full shadow-xl border border-emerald-100/80 flex items-center gap-2 text-[11px] sm:text-xs font-semibold text-slate-800"
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span>Instant GST Invoicing</span>
                </motion.div>

                {/* Floating Badge 2 - Bottom Right */}
                <motion.div
                  animate={{ y: [0, 6, 0] }}
                  transition={{ repeat: Infinity, duration: 5, ease: "easeInOut" }}
                  className="absolute -bottom-3 right-0 sm:-bottom-5 sm:right-2 z-30 bg-white/95 backdrop-blur-md px-3 sm:px-4 py-1.5 rounded-full shadow-xl border border-orange-100/80 flex items-center gap-2 text-[11px] sm:text-xs font-semibold text-slate-800"
                >
                  <span className="w-2 h-2 rounded-full bg-[#DF5D01] animate-pulse" />
                  <span>Real-time Mobile Sync</span>
                </motion.div>

                {/* Front Phone Image (Dashboard) */}
                <div className="w-[46%] sm:w-[47%] max-w-[230px] sm:max-w-[270px] relative z-10 rounded-[26px] sm:rounded-[38px] overflow-hidden shadow-[0_22px_45px_-8px_rgba(0,0,0,0.25)] border border-slate-200/80 transition-all duration-500 hover:scale-[1.03] hover:-translate-y-3">
                  <img
                    src="/images/app-screen-1.png"
                    alt="ABSS App Dashboard"
                    className="w-full h-auto block rounded-[26px] sm:rounded-[38px]"
                  />
                </div>

                {/* Back Phone Image (Settings) */}
                <div className="w-[46%] sm:w-[47%] max-w-[230px] sm:max-w-[270px] relative z-0 mt-[12%] sm:mt-[15%] rounded-[26px] sm:rounded-[38px] overflow-hidden shadow-[0_22px_45px_-8px_rgba(0,0,0,0.25)] border border-slate-200/80 transition-all duration-500 hover:scale-[1.03] hover:-translate-y-3">
                  <img
                    src="/images/app-screen-2.png"
                    alt="ABSS App Settings"
                    className="w-full h-auto block rounded-[26px] sm:rounded-[38px]"
                  />
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
}
